import { useEffect, useRef } from "react";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

const iconCache = {};

/**
 * Google MarkerClusterer default colors (DefaultRenderer):
 * blue normally, red when cluster is larger than the mean.
 */
const GOOGLE_CLUSTER_BLUE = "#0000ff";
const GOOGLE_CLUSTER_RED = "#ff0000";

const getClusterTypeCounts = (clusterMarkers = []) => {
	const counts = { group: 0, customer: 0, prospect: 0, other: 0 };
	clusterMarkers.forEach((marker) => {
		const type = marker?.__kycMarkerData?.type;
		if (type === "group") {
			counts.group += 1;
			return;
		}
		if (type === "prospect") {
			counts.prospect += 1;
			return;
		}
		if (type === "other") {
			counts.other += 1;
			return;
		}
		counts.customer += 1;
	});
	return counts;
};

const buildClusterTooltipHtml = (counts = {}) => `
	<div class="kyc-map-cluster__tooltip" role="tooltip">
		<div class="kyc-map-cluster__tooltip-row">
			<span class="kyc-map-cluster__tooltip-dot is-group"></span>
			<span class="kyc-map-cluster__tooltip-label">Group Company</span>
			<span class="kyc-map-cluster__tooltip-count">${Number(counts.group || 0)}</span>
		</div>
		<div class="kyc-map-cluster__tooltip-row">
			<span class="kyc-map-cluster__tooltip-dot is-customer"></span>
			<span class="kyc-map-cluster__tooltip-label">Customer</span>
			<span class="kyc-map-cluster__tooltip-count">${Number(counts.customer || 0)}</span>
		</div>
		<div class="kyc-map-cluster__tooltip-row">
			<span class="kyc-map-cluster__tooltip-dot is-prospect"></span>
			<span class="kyc-map-cluster__tooltip-label">Prospect</span>
			<span class="kyc-map-cluster__tooltip-count">${Number(counts.prospect || 0)}</span>
		</div>
		<div class="kyc-map-cluster__tooltip-row">
			<span class="kyc-map-cluster__tooltip-dot is-other"></span>
			<span class="kyc-map-cluster__tooltip-label">Others</span>
			<span class="kyc-map-cluster__tooltip-count">${Number(counts.other || 0)}</span>
		</div>
	</div>
`;

const getCachedMarkerIcon = (getGoogleMarkerIcon, type) => {
	const mapsReady = Boolean(window.google?.maps);
	const cacheKey = `${mapsReady ? "g" : "p"}-${type}`;
	if (!iconCache[cacheKey]) {
		iconCache[cacheKey] = getGoogleMarkerIcon(type);
	}
	return iconCache[cacheKey];
};

const getClusterColor = (count, mean = 0) =>
	count > Math.max(10, mean) ? GOOGLE_CLUSTER_RED : GOOGLE_CLUSTER_BLUE;

const getClusterSize = () => 45;

/**
 * HTML OverlayView cluster marker — enables CSS ripple + smooth transitions.
 * Compatible with MarkerClusterer (setMap / getPosition / addListener / getVisible).
 */
const createKycClusterOverlayClass = () => {
	class KycClusterOverlay extends window.google.maps.OverlayView {
		constructor({ position, count, color, size, typeCounts }) {
			super();
			this.position_ =
				position instanceof window.google.maps.LatLng
					? position
					: new window.google.maps.LatLng(position.lat, position.lng);
			this.count = count;
			this.color = color;
			this.size = size;
			this.typeCounts = typeCounts || { group: 0, customer: 0, prospect: 0, other: 0 };
			this.div = null;
			this.visible_ = true;
		}

		onAdd() {
			const div = document.createElement("div");
			div.className = "kyc-map-cluster";
			div.style.setProperty("--cluster-color", this.color);
			div.style.setProperty("--cluster-size", `${this.size}px`);
			div.setAttribute(
				"aria-label",
				`${this.count} locations. Group Company ${this.typeCounts.group}, Customer ${this.typeCounts.customer}, Prospect ${this.typeCounts.prospect}, Others ${this.typeCounts.other}`
			);
			div.innerHTML = `
				<span class="kyc-map-cluster__ring kyc-map-cluster__ring--outer" aria-hidden="true"></span>
				<span class="kyc-map-cluster__ring kyc-map-cluster__ring--mid" aria-hidden="true"></span>
				<span class="kyc-map-cluster__ripple" aria-hidden="true"></span>
				<span class="kyc-map-cluster__ripple kyc-map-cluster__ripple--delay" aria-hidden="true"></span>
				<span class="kyc-map-cluster__core">${this.count}</span>
				${buildClusterTooltipHtml(this.typeCounts)}
			`;

			div.addEventListener("click", (event) => {
				window.google.maps.event.trigger(this, "click", event);
			});

			this.div = div;
			this.getPanes().overlayMouseTarget.appendChild(div);
		}

		draw() {
			if (!this.div) return;
			const projection = this.getProjection();
			if (!projection) return;

			const point = projection.fromLatLngToDivPixel(this.position_);
			if (!point) return;

			const half = this.size / 2;
			this.div.style.transform = `translate(${point.x - half}px, ${point.y - half}px)`;
		}

		onRemove() {
			if (this.div) {
				this.div.remove();
				this.div = null;
			}
		}

		getPosition() {
			return this.position_;
		}

		getVisible() {
			return this.visible_;
		}

		setVisible(visible) {
			this.visible_ = visible;
			if (this.div) {
				this.div.style.visibility = visible ? "visible" : "hidden";
			}
		}
	}

	return KycClusterOverlay;
};

const createKycClusterRenderer = () => {
	const KycClusterOverlay = createKycClusterOverlayClass();

	return {
		render({ count, position, markers }, stats) {
			const mean = stats?.clusters?.markers?.mean || 0;
			const color = getClusterColor(count, mean);
			const size = getClusterSize(count);
			const typeCounts = getClusterTypeCounts(markers);

			return new KycClusterOverlay({
				position,
				count,
				color,
				size,
				typeCounts,
			});
		},
	};
};

/**
 * Imperatively manages company/group markers with clustering.
 * Selected marker uses black-tick without remounting the whole cluster.
 */
const useKycMapCompanyCluster = ({
	map,
	markers,
	selectedMarkerId = null,
	getGoogleMarkerIcon,
	onMarkerClick,
	onMarkerMouseOver,
	onMarkerMouseOut,
}) => {
	const onMarkerClickRef = useRef(onMarkerClick);
	const onMarkerMouseOverRef = useRef(onMarkerMouseOver);
	const onMarkerMouseOutRef = useRef(onMarkerMouseOut);
	const googleMarkersRef = useRef([]);
	const selectedMarkerIdRef = useRef(selectedMarkerId);

	onMarkerClickRef.current = onMarkerClick;
	onMarkerMouseOverRef.current = onMarkerMouseOver;
	onMarkerMouseOutRef.current = onMarkerMouseOut;
	selectedMarkerIdRef.current = selectedMarkerId;

	useEffect(() => {
		if (!map || !window.google?.maps) {
			return undefined;
		}

		const googleMarkers = (markers || []).map((data) => {
			const isSelected = selectedMarkerIdRef.current && data.id === selectedMarkerIdRef.current;
			const marker = new window.google.maps.Marker({
				position: data.position,
				title: data.title,
				icon: getCachedMarkerIcon(
					getGoogleMarkerIcon,
					isSelected ? "selected" : data.type
				),
				optimized: false,
				zIndex: isSelected ? 999 : undefined,
			});

			marker.__kycMarkerData = data;

			marker.addListener("click", () => {
				onMarkerClickRef.current?.(data);
			});
			marker.addListener("mouseover", () => {
				onMarkerMouseOverRef.current?.(data);
			});
			marker.addListener("mouseout", () => {
				onMarkerMouseOutRef.current?.(data);
			});

			return marker;
		});

		googleMarkersRef.current = googleMarkers;

		const clusterer = new MarkerClusterer({
			map,
			markers: googleMarkers,
			renderer: createKycClusterRenderer(),
		});

		return () => {
			clusterer.clearMarkers(true);
			clusterer.setMap(null);
			googleMarkers.forEach((marker) => {
				window.google.maps.event.clearInstanceListeners(marker);
				marker.setMap(null);
			});
			googleMarkersRef.current = [];
		};
	}, [map, markers, getGoogleMarkerIcon]);

	useEffect(() => {
		googleMarkersRef.current.forEach((marker) => {
			const data = marker.__kycMarkerData;
			if (!data) return;

			const isSelected = selectedMarkerId && data.id === selectedMarkerId;
			marker.setIcon(
				getCachedMarkerIcon(getGoogleMarkerIcon, isSelected ? "selected" : data.type)
			);
			marker.setZIndex(isSelected ? 999 : null);
		});
	}, [selectedMarkerId, getGoogleMarkerIcon]);
};

export default useKycMapCompanyCluster;
